import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import { useNotification } from './Notification';
import './TagSelector.css';

function EquipmentSelector({ tenantId, selectedEquipment = [], onChange, make, multiple = true }) {
  const { showNotification } = useNotification();
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newEquipmentName, setNewEquipmentName] = useState('');
  const [newEquipmentColor, setNewEquipmentColor] = useState('#4ECDC4');
  const containerRef = useRef(null);

  useEffect(() => {
    if (tenantId) {
      loadEquipment();
    }
  }, [tenantId, make]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
        setShowCreateForm(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const loadEquipment = async () => {
    try {
      setLoading(true);
      const params = make ? `?make=${encodeURIComponent(make)}` : '';
      const data = await api.get(`/equipment${params}`, tenantId);
      setEquipment(data);
    } catch (error) {
      console.error('Error loading equipment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEquipmentToggle = (equipmentId) => {
    if (multiple) {
      const newSelected = selectedEquipment.includes(equipmentId)
        ? selectedEquipment.filter(id => id !== equipmentId)
        : [...selectedEquipment, equipmentId];
      onChange(newSelected);
    } else {
      onChange(selectedEquipment.includes(equipmentId) ? [] : [equipmentId]);
      setShowDropdown(false);
    }
  };

  const removeEquipment = (equipmentId) => {
    const newSelected = selectedEquipment.filter(id => id !== equipmentId);
    onChange(newSelected);
  };

  const handleCreateEquipment = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!newEquipmentName.trim()) {
      showNotification('Bitte geben Sie einen Namen für die Sonderaustattung ein', 'error');
      return;
    }
    try {
      const newEquipment = await api.post('/equipment', {
        name: newEquipmentName.trim(),
        make: make || null,
        color: newEquipmentColor
      }, tenantId);
      setEquipment([...equipment, newEquipment]);
      setNewEquipmentName('');
      setNewEquipmentColor('#4ECDC4');
      setShowCreateForm(false);
      const newSelected = [...selectedEquipment, newEquipment.id];
      onChange(newSelected);
      showNotification('Sonderaustattung erfolgreich erstellt', 'success');
    } catch (error) {
      console.error('Error creating equipment:', error);
      showNotification(error.response?.data?.error || error.message || 'Fehler beim Erstellen der Sonderaustattung', 'error');
    }
  };

  const presetColors = [
    '#4ECDC4', '#45B7D1', '#FFA07A', '#FFD93D', '#C77DFF',
    '#6BCF7F', '#FF8C94', '#9B8DFF', '#FFB84D', '#FF6B6B'
  ];

  const selectedEquipmentObjects = equipment.filter(eq => selectedEquipment.includes(eq.id));
  const availableEquipment = equipment.filter(eq => !selectedEquipment.includes(eq.id));

  if (loading) {
    return <div className="tag-selector-loading">Lade Sonderaustattungen...</div>;
  }

  return (
    <div className="tag-selector" ref={containerRef}>
      <label className="tag-selector-label">Sonderaustattungen</label>
      {make && (
        <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>
          Gefiltert nach Marke: {make}
        </div>
      )}
      <div className="tag-selector-container">
        <div className="selected-tags">
          {selectedEquipmentObjects.map(eq => (
            <span
              key={eq.id}
              className="tag-badge selected"
              style={{ backgroundColor: eq.color }}
              onClick={() => removeEquipment(eq.id)}
              title="Klicken zum Entfernen"
            >
              {eq.name}
            </span>
          ))}
          <button
            type="button"
            className="tag-selector-toggle"
            onClick={() => setShowDropdown(!showDropdown)}
          >
            + Sonderaustattung hinzufügen
          </button>
        </div>
        {showDropdown && (
          <div className="tag-dropdown" onClick={(e) => e.stopPropagation()}>
            {showCreateForm ? (
              <div className="tag-create-form" onClick={(e) => e.stopPropagation()}>
                <form onSubmit={handleCreateEquipment}>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <input
                      type="text"
                      value={newEquipmentName}
                      onChange={(e) => setNewEquipmentName(e.target.value)}
                      placeholder="Sonderaustattungs-Name"
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '0.875rem'
                      }}
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                    <input
                      type="color"
                      value={newEquipmentColor}
                      onChange={(e) => setNewEquipmentColor(e.target.value)}
                      style={{
                        width: '40px',
                        height: '40px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  {make && (
                    <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.5rem' }}>
                      Wird für Marke "{make}" erstellt
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                    {presetColors.map(color => (
                      <button
                        key={color}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setNewEquipmentColor(color);
                        }}
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '4px',
                          border: `2px solid ${newEquipmentColor === color ? '#333' : '#ddd'}`,
                          backgroundColor: color,
                          cursor: 'pointer'
                        }}
                      />
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="submit"
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#2196f3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      Erstellen
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowCreateForm(false);
                        setNewEquipmentName('');
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#f5f5f5',
                        color: '#333',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      Abbrechen
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <>
                <div
                  className="tag-dropdown-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowCreateForm(true);
                  }}
                  style={{ borderBottom: '1px solid #eee', fontWeight: 500 }}
                >
                  + Neue Sonderaustattung erstellen
                </div>
                {availableEquipment.length === 0 ? (
                  <div className="tag-dropdown-empty">
                    {make ? `Keine Sonderaustattungen für ${make} verfügbar` : 'Keine weiteren Sonderaustattungen verfügbar'}
                  </div>
                ) : (
                  availableEquipment.map(eq => (
                    <div
                      key={eq.id}
                      className="tag-dropdown-item"
                      onClick={() => handleEquipmentToggle(eq.id)}
                    >
                      <span
                        className="tag-badge"
                        style={{ backgroundColor: eq.color }}
                      >
                        {eq.name}
                      </span>
                      {eq.make && (
                        <span style={{ fontSize: '0.75rem', color: '#999', marginLeft: '0.5rem' }}>
                          ({eq.make})
                        </span>
                      )}
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default EquipmentSelector;
